'use client';

/**
 * PlpGiftBottomSheetContent
 *
 * Self-contained component for the PLP gift selection bottom sheet.
 * Renders the same "round 1" UI as the PDP ProductGift component:
 * - Quantity stepper (reactive)
 * - Flat list of gift rules with radio-select per rule
 * - Auto-selects best gift on mount and on quantity change
 *
 * This component manages its own state internally so it works correctly
 * when injected via bottomSheet.inject() (avoids stale closure issues).
 */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { observer } from '@legendapp/state/react';
import { ProductGiftContents } from '../product-gift-contents/product-gift-contents';
import { CartCounterButtonWidget } from '../cart-counter-button/cart-counter-button-widget';
import { CartCounterButtonEnum, type GiftLayoutEnum } from '../../../../enums';
import { useGiftSelection } from '../../hooks/use-gift-selection.hook';
import { giftSelectionStoreAPI } from '../../stores/simple-gift-selection-store.public-api';
import type { FreeGiftsData } from '../../../../types/front.graphql';
import type { SelectedGift } from '../../types/gift-selection.types';
import cssStyles from './product-listing-quick-add-gift-widget.module.css';

export interface PlpGiftBottomSheetContentProps {
    /** Session ID for this gift selection */
    sessionId: string;
    /** Sorted gift rules data */
    sortedGiftsDataList: FreeGiftsData[];
    /** Available gifts for session updates */
    availableGifts: FreeGiftsData[];
    /** Callback to notify parent of quantity changes */
    onQuantityChange?: (quantity: number) => void;
    /** Layout variant */
    layout?: GiftLayoutEnum;
}

export const PlpGiftBottomSheetContent: React.FC<PlpGiftBottomSheetContentProps> = observer(
    ({ sessionId, sortedGiftsDataList, availableGifts, onQuantityChange, layout }) => {
        const giftHook = useGiftSelection();
        const [counterValue, setCounterValue] = useState(1);
        const isInitializedRef = useRef(false);

        // Auto-select best gift on first render
        useEffect(() => {
            if (!isInitializedRef.current && sessionId && sortedGiftsDataList.length > 0) {
                isInitializedRef.current = true;
                giftHook.autoSelectBestGift(sessionId, 1);
            }
        }, [sessionId, sortedGiftsDataList, giftHook]);

        // Get observable for selected gifts
        // IMPORTANT: Access the store observable directly via proxy instead of
        // using getStoreObservable() which relies on peek() and can return undefined
        // when the persisted store (IndexedDB) hasn't fully synchronized yet.
        // Legend-State proxy creates a valid observable even before data is set.
        const storeObs$ = giftSelectionStoreAPI.state.getStoreObservable();
        const selectedGifts$ = storeObs$.sessions[sessionId].selectedGifts;

        // Handle gift selection (round 1)
        const handleSelectGift = useCallback(
            (gift: SelectedGift) => {
                giftHook.selectGift(sessionId, gift, 1);
            },
            [giftHook, sessionId]
        );

        // Quantity increase handler
        const handleIncrease = useCallback(() => {
            setCounterValue(prev => {
                const newValue = prev + 1;

                // Update session with new quantity
                if (sessionId && giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
                    giftHook.updateSession(sessionId, availableGifts, newValue);
                    giftHook.clearSession(sessionId);
                    giftHook.autoSelectBestGift(sessionId, 1);
                }

                onQuantityChange?.(newValue);
                return newValue;
            });
        }, [sessionId, availableGifts, giftHook, onQuantityChange]);

        // Quantity decrease handler
        const handleDecrease = useCallback(() => {
            setCounterValue(prev => {
                if (prev <= 1) return prev;
                const newValue = prev - 1;

                // Update session with new quantity
                if (sessionId && giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
                    giftHook.updateSession(sessionId, availableGifts, newValue);
                    giftHook.clearSession(sessionId);
                    giftHook.autoSelectBestGift(sessionId, 1);
                }

                onQuantityChange?.(newValue);
                return newValue;
            });
        }, [sessionId, availableGifts, giftHook, onQuantityChange]);

        // Quantity direct value change handler
        const handleValueChange = useCallback(
            (value: number) => {
                const newValue = Math.max(1, value);
                setCounterValue(newValue);

                // Update session with new quantity
                if (sessionId && giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
                    giftHook.updateSession(sessionId, availableGifts, newValue);
                    giftHook.clearSession(sessionId);
                    giftHook.autoSelectBestGift(sessionId, 1);
                }

                onQuantityChange?.(newValue);
            },
            [sessionId, availableGifts, giftHook, onQuantityChange]
        );

        // Auto-fill handler: when user selects a gift that requires more quantity
        const handleAutoFillRequired = useCallback(
            (requiredQuantity: number, gift: SelectedGift) => {
                // 1. Update local quantity state
                setCounterValue(requiredQuantity);

                // 2. Update session with new quantity
                if (sessionId && giftSelectionStoreAPI.computed.sessionExists(sessionId)) {
                    giftHook.updateSession(sessionId, availableGifts, requiredQuantity);
                }

                // 3. Recalculate and select the gift with updated quantity
                const rule = gift.giftData?.rule;
                if (rule) {
                    const buyX = rule.discount_step || 1;
                    const getY = rule.discount_amount || 1;
                    const eligibleSets = Math.floor(requiredQuantity / buyX);
                    const maxFreeGifts = eligibleSets * getY;

                    const updatedGift: SelectedGift = {
                        ...gift,
                        quantity: maxFreeGifts,
                        giftSlots: maxFreeGifts,
                        quantityUsedByRule: eligibleSets * buyX,
                    };

                    giftHook.selectGift(sessionId, updatedGift, 1);
                }

                onQuantityChange?.(requiredQuantity);
            },
            [sessionId, availableGifts, giftHook, onQuantityChange]
        );

        return (
            <div className={cssStyles.bottomSheetInjectContent}>
                <div className={cssStyles.quantityStepperContainer}>
                    <CartCounterButtonWidget
                        value={counterValue}
                        handleIncrease={handleIncrease}
                        handleDecrease={handleDecrease}
                        emitValueChange={handleValueChange}
                        variant={CartCounterButtonEnum.COUNTER}
                    />
                </div>
                <ProductGiftContents
                    layout={layout}
                    counterValue={counterValue}
                    selectedGifts$={selectedGifts$}
                    sortedGiftsDataList={sortedGiftsDataList}
                    onSelectGift={handleSelectGift}
                    sessionId={sessionId}
                    enableAutoFill={true}
                    onAutoFillRequired={handleAutoFillRequired}
                />
            </div>
        );
    }
);

PlpGiftBottomSheetContent.displayName = 'PlpGiftBottomSheetContent';
